import { useState } from "react";

import {
  defaultTemplate,
  isValidFormRedirectUrl,
} from "@smart/bridge-templates-basic";
import { FormTranslation } from "@smart/bridge-types-basic";
import {
  Displayer,
  DigitalSignatureLogo,
  Modal,
} from "@smart/itops-sb-design-system-dom";
import { DIGITAL_SIGNATURE_RETURN_TOKEN_PARAM } from "@smart/itops-utils-basic";

import { AutofillFeedback } from "./autofill-components";
import { IntakePrintProps, useIntakePrint } from "./print-components";
import { PageWrapper, Section } from "./section-components";
import { signatureLogoVariantFromSource } from "../hooks";
import { AutoFillStatus, FieldItem, SubmissionItem, TeamItem } from "../types";
import { getClientNamesFromResponses } from "../utils";
import { getUITranslation, splitUITranslation } from "../utils/ui-translations";

export type IntakeStatusProps = {
  isMatterClosed?: boolean;
  team: TeamItem;
  response?: string;
  category?: string;
  status?: SubmissionItem["status"];
  deleted?: boolean | null;
  printOptions?: IntakePrintProps;
  autoFillStatus?: AutoFillStatus;
  onSubmitAutofillFeedback?: (feedback?: string) => Promise<void>;
  selectedLanguage?: string;
  translations?: FormTranslation[];
  recipient?: SubmissionItem["recipient"];
  fields?: FieldItem[];
  responses?: Record<string, unknown>;
  formMatterTypes?: { representativeOptions?: string[] }[];
  hasPendingDigitalSignature?: boolean;
  signingUrl?: string;
  onSignatureModalClose?: () => void;
};

export const IntakeStatus = ({
  isMatterClosed,
  team,
  response,
  category,
  status,
  deleted,
  printOptions,
  autoFillStatus,
  onSubmitAutofillFeedback,
  selectedLanguage,
  translations,
  recipient,
  fields,
  responses,
  formMatterTypes,
  hasPendingDigitalSignature,
  signingUrl,
  onSignatureModalClose,
}: IntakeStatusProps) => {
  const { onPrint, elements } = useIntakePrint(printOptions);
  const signatureLogoVariant = signatureLogoVariantFromSource(team.source);
  const [dismissDigitalSignatureMessage, setDismissDigitalSignatureMessage] =
    useState(false);
  /** The user will be redirected back here once they've finished signing.
   * The webhook that clears hasPendingDigitalSignature may not have run yet.
   * Treat arriving via that redirect as proof signing is done, rather than briefly showing
   * the modal while the webhook catches up.
   */
  const justReturnedFromSigning = new URLSearchParams(
    window.location.search,
  ).has(DIGITAL_SIGNATURE_RETURN_TOKEN_PARAM);
  const showDigitalSignatureModal =
    !!hasPendingDigitalSignature &&
    !justReturnedFromSigning &&
    !deleted &&
    !isMatterClosed &&
    status === "completed" &&
    !dismissDigitalSignatureMessage;

  const resumeSigningUrl =
    signingUrl && isValidFormRedirectUrl(signingUrl) ? signingUrl : undefined;

  const handleContinueToSigning = () => {
    if (resumeSigningUrl) window.location.assign(resumeSigningUrl);
  };

  const getClientNames = () => {
    if (recipient) {
      return {
        firstName: recipient.firstName?.trim() ?? "",
        lastName: recipient.lastName?.trim() ?? "",
      };
    }
    if (fields && responses) {
      return getClientNamesFromResponses(fields, responses, formMatterTypes);
    }
    return {
      firstName: "",
      lastName: "",
    };
  };

  const { firstName, lastName } = getClientNames();

  const translatedResponse =
    selectedLanguage && selectedLanguage !== "en"
      ? translations?.find((t) => t.language === selectedLanguage)?.response
      : undefined;
  let statusText = getUITranslation("formHasBeenCompleted", selectedLanguage);
  if (isMatterClosed || status === "cancelled")
    statusText = getUITranslation("formHasBeenClosed", selectedLanguage);
  if (status === "draft")
    statusText = getUITranslation(
      "formNotCurrentlyAvailable",
      selectedLanguage,
    );
  if (deleted)
    statusText = getUITranslation("formHasBeenRemoved", selectedLanguage);

  const contactName =
    team.name || getUITranslation("contactFallbackUs", selectedLanguage);
  const contactDisplay = team.email ? (
    <a href={`mailto:${team.email}`}>{contactName}</a>
  ) : (
    <span>{contactName}</span>
  );
  const [beforeSigningAction, afterSigningAction] = splitUITranslation(
    "signatureModalRedirect",
    "action",
    selectedLanguage,
  );
  const [beforeModalContact, afterModalContact] = splitUITranslation(
    "signatureModalContact",
    "contact",
    selectedLanguage,
  );
  const [beforeStatusContact, afterStatusContact] = splitUITranslation(
    "formStatusMessage",
    "contact",
    selectedLanguage,
    { status: statusText },
  );

  return (
    <PageWrapper sidebar={false}>
      <div className="h-full w-full flex justify-center p-[1.6rem]">
        <Section
          dataTestId="status-content"
          className="bg-white rounded max-w-[85rem] w-full h-full py-[4rem] px-[2.4rem]"
        >
          <Modal
            dataTestId="digital-signature-modal"
            header={{
              text: getUITranslation("signatureRequired", selectedLanguage),
              icon: "solidSignature",
              iconBgColor: "bg-cyan-140",
            }}
            footer={{
              buttons: [
                ...(resumeSigningUrl
                  ? [
                      {
                        text: getUITranslation(
                          "continueLabel",
                          selectedLanguage,
                        ),
                        variant: "primaryDefault" as const,
                        onClick: handleContinueToSigning,
                      },
                    ]
                  : []),
              ],
            }}
            open={showDigitalSignatureModal}
            onClose={() => {
              setDismissDigitalSignatureMessage(true);
              onSignatureModalClose?.();
            }}
          >
            <div className="flex justify-center items-center rounded bg-blue-60/50 w-full h-[10rem]">
              <DigitalSignatureLogo
                variant={signatureLogoVariant}
                className="w-[12rem]"
              />
            </div>
            {resumeSigningUrl ? (
              <p>
                {beforeSigningAction}
                <strong>
                  {getUITranslation("continueLabel", selectedLanguage)}
                </strong>
                {afterSigningAction}
              </p>
            ) : (
              <p>
                {beforeModalContact}
                {contactDisplay}
                {afterModalContact}
              </p>
            )}
          </Modal>
          {!deleted && status === "completed" && (
            <div className="flex flex-col h-full gap-[3.2rem] text-paragraph mx-auto">
              <Displayer
                paragraphTag="p"
                value={translatedResponse || response}
                fallback={defaultTemplate.response}
                placeholderData={{
                  downloadFormLink: "#download",
                  firmName: team.name,
                  firmPhone: team.phone || "",
                  matterCategory: category || "matter",
                  clientFirstName: firstName,
                  clientLastName: lastName,
                }}
                onPlaceholderClick={(e, element) => {
                  if (element.name === "downloadFormLink") {
                    e.preventDefault();
                    onPrint();
                  }
                }}
              />
            </div>
          )}
          {(deleted || isMatterClosed || status !== "completed") && (
            <p>
              {beforeStatusContact}
              {contactDisplay}
              {afterStatusContact}
            </p>
          )}
          <AutofillFeedback
            open={autoFillStatus === "completed"}
            onSubmit={onSubmitAutofillFeedback}
          />
        </Section>
      </div>
      {elements}
    </PageWrapper>
  );
};
